"use client";

import React, { useState } from "react";
import { useUser } from "@/context/useUser";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
    User, Mail, CalendarDays, MapPin,
    Building2, Phone, IdCard, Briefcase, Globe,
    ShoppingBag,
    ArrowRight,
    Edit2,
    KeyRound,
    Loader2
} from "lucide-react";
import { format } from "date-fns";
import { id, enUS } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import toast from "react-hot-toast";
import { axiosClient } from "@/lib/axiosClient";

// --- Interfaces ---
interface Registration {
    registration_id: number;
    registration_date: string;
    registration_number: string;
    payment_status: string;
    package?: {
        package_name: string;
        price: string;
        currency: string;
    };
    period?: {
        name: string;
    };
}

interface Participant {
    full_name: string;
    nik: string;
    birth_place: string;
    birth_date: string;
    phone_mobile: string;
    address: string;
    institution: string;
    position: string;
    religion?: { name: string };
    country?: { name: string };
    registration?: Registration[];
}

interface UserData {
    name: string;
    email: string;
    created_at: string;
    role_id: number;
    role?: { role_name: string };
    participant?: Participant;
}

export default function ProfilePage() {
    const { t, i18n } = useTranslation('private');
    const navigate = useNavigate();
    const { data: user, isLoading: userLoading, refetch } = useUser() as {
        data: UserData | null;
        isLoading: boolean;
        refetch: () => void;
    };

    // Hanya Administrator (1) dan Pendaftar (2) yang punya keperluan memilih
    // paket - PIC Kolektif (3) punya tombol "Kelola Partisipan" sendiri.
    // Whitelist (bukan blacklist role_id !== 3) supaya role baru (Operator
    // Conference, Petugas Presensi, Undangan Event, Petugas RFID, dst) tidak
    // ikut kebawa tampil tombol/section paket ini.
    const canOrderPackage = Boolean(user) && [1, 2].includes(user!.role_id);

    const [isEditProfileOpen, setIsEditProfileOpen] = useState(false);
    const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);

    // Profile Form State
    const [profileData, setProfileData] = useState({
        name: "",
        email: "",
        full_name: "",
        phone_mobile: "",
        address: "",
        institution: "",
        position: "",
        birth_place: "",
        birth_date: "",
    });

    // Password Form State
    const [passwordData, setPasswordData] = useState({
        current_password: "",
        new_password: "",
        new_password_confirmation: "",
    });

    const [isSavingProfile, setIsSavingProfile] = useState(false);
    const [isSavingPassword, setIsSavingPassword] = useState(false);

    const formatDate = (date?: string | Date): string =>
        date ? format(new Date(date), "d MMMM yyyy", {
            locale: i18n.language === 'id' ? id : enUS
        }) : "-";

    const handleOpenEditProfile = () => {
        if (user) {
            setProfileData({
                name: user.name || "",
                email: user.email || "",
                full_name: user.participant?.full_name || "",
                phone_mobile: user.participant?.phone_mobile || "",
                address: user.participant?.address || "",
                institution: user.participant?.institution || "",
                position: user.participant?.position || "",
                birth_place: user.participant?.birth_place || "",
                birth_date: user.participant?.birth_date ? String(user.participant.birth_date).substring(0, 10) : "",
            });
        }
        setIsEditProfileOpen(true);
    };

    const handleSaveProfile = async () => {
        try {
            setIsSavingProfile(true);
            await axiosClient.put("/users/profile", profileData);
            toast.success("Profil berhasil diperbarui!");
            setIsEditProfileOpen(false);
            refetch(); // Refresh user data
        } catch (error: any) {
            toast.error(error?.response?.data?.message || "Gagal memperbarui profil.");
        } finally {
            setIsSavingProfile(false);
        }
    };

    const handleSavePassword = async () => {
        if (passwordData.new_password !== passwordData.new_password_confirmation) {
            toast.error("Konfirmasi password baru tidak cocok.");
            return;
        }

        try {
            setIsSavingPassword(true);
            await axiosClient.post("/users/change-password", passwordData);
            toast.success("Password berhasil diubah!");
            setIsChangePasswordOpen(false);
            setPasswordData({ current_password: "", new_password: "", new_password_confirmation: "" });
        } catch (error: any) {
            toast.error(error?.response?.data?.message || "Gagal mengubah password.");
        } finally {
            setIsSavingPassword(false);
        }
    };

    if (userLoading) {
        return (
            <div className="max-w-5xl mx-auto space-y-4 p-4">
                <Skeleton className="h-48 w-full rounded-2xl" />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Skeleton className="h-64 md:col-span-2 rounded-xl" />
                    <Skeleton className="h-64 rounded-xl" />
                </div>
            </div>
        );
    }

    if (!user) return null;
    const participant = user.participant;

    return (
        <div className="mx-auto space-y-4 animate-in fade-in duration-500 pb-20">

            {/* --- HEADER SECTION --- */}
            <div className="relative overflow-hidden rounded-2xl bg-slate-900 p-6 md:p-8 text-white shadow-xl">
                <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/10 blur-[100px] -mr-32 -mt-32" />
                <div className="relative z-10 flex flex-col md:flex-row items-center gap-6 text-center md:text-left justify-between">
                    <div className="flex flex-col md:flex-row items-center gap-6">
                        <div className="flex-none h-20 w-20 md:h-24 md:w-24 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center text-3xl font-black shadow-inner">
                            {user.name ? user.name.charAt(0).toUpperCase() : "U"}
                        </div>
                        <div className="flex-1">
                            <Badge className="bg-blue-600 mb-2 text-[9px] uppercase tracking-widest px-2 py-1 border-none">
                                {user?.role?.role_name ?? "Member"}
                            </Badge>
                            <h1 className="text-xl md:text-2xl font-bold tracking-tight leading-tight break-words">
                                {user.name}
                            </h1>
                            <div className="mt-2 flex flex-wrap justify-center md:justify-start gap-x-4 text-slate-400 text-[11px] font-medium">
                                <span className="flex items-center gap-1.5"><Mail className="w-3 h-3 text-blue-400" /> {user.email}</span>
                                <span className="flex items-center gap-1.5"><CalendarDays className="w-3 h-3 text-indigo-400" /> {t('profile.joined')}: {formatDate(user.created_at)}</span>
                            </div>
                        </div>
                    </div>

                    {/* Action buttons removed from here and moved to bottom of personal data */}
                </div>
            </div>

            {/* --- DETAILS GRID --- */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch text-left">

                {/* Kolom Kiri: Data Personal */}
                <div className="md:col-span-2">
                    <SectionBox title={t('profile.sections.personal')} icon={<IdCard className="w-4 h-4 text-blue-500" />}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-6">
                            <InfoItem label={t('profile.labels.full_name')} value={participant?.full_name} icon={<User />} />
                            <InfoItem label={t('profile.labels.nik')} value={participant?.nik} icon={<IdCard />} />
                            <InfoItem label={t('profile.labels.citizenship')} value={participant?.country?.name} icon={<Globe />} />
                            <InfoItem label={t('profile.labels.birth_info')} value={`${participant?.birth_place || "-"}, ${formatDate(participant?.birth_date)}`} />
                            <InfoItem label={t('profile.labels.religion')} value={participant?.religion?.name} isFullWidth />
                            <InfoItem label={t('profile.labels.address')} value={participant?.address} icon={<MapPin />} isFullWidth />
                            
                            {/* Action Buttons */}
                            <div className="sm:col-span-2 mt-2 pt-6 border-t border-slate-100 dark:border-slate-800 flex flex-col sm:flex-row justify-end gap-3">
                                <Button
                                    onClick={() => setIsChangePasswordOpen(true)}
                                    variant="outline"
                                    className="w-full sm:w-auto text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 border-slate-200 dark:border-slate-700"
                                >
                                    <KeyRound className="w-4 h-4 mr-2" /> Ganti Password
                                </Button>
                                <Button
                                    onClick={handleOpenEditProfile}
                                    className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white shadow-sm shadow-blue-600/20"
                                >
                                    <Edit2 className="w-4 h-4 mr-2" /> Edit Profil
                                </Button>
                            </div>
                        </div>
                    </SectionBox>
                </div>

                {/* Kolom Kanan: Karir & Kontak */}
                <div className="flex flex-col gap-4 h-full">
                    <SectionBox title={t('profile.sections.career')} icon={<Briefcase className="w-4 h-4 text-amber-500" />} className="flex-1">
                        <div className="space-y-6">
                            <InfoItem label={t('profile.labels.institution')} value={participant?.institution} icon={<Building2 />} />
                            <InfoItem label={t('profile.labels.position')} value={participant?.position} icon={<Briefcase />} />
                        </div>
                    </SectionBox>

                    <SectionBox title={t('profile.sections.contact')} icon={<Phone className="w-4 h-4 text-emerald-500" />}>
                        <InfoItem
                            label={t('profile.labels.whatsapp')}
                            value={participant?.phone_mobile}
                            icon={<Phone className="text-emerald-600" />}
                        />
                    </SectionBox>
                </div>
            </div>

            {/* --- PACKAGE REGISTRATIONS (NON-PIC ONLY) --- */}
            {canOrderPackage && participant?.registration && participant.registration.length > 0 && (
                <div className="mt-4">
                    <SectionBox title="Paket Kegiatan yang Diikuti" icon={<ShoppingBag className="w-4 h-4 text-purple-500" />}>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {participant.registration.map((reg) => (
                                <div key={reg.registration_id} className="group relative p-4 pl-5 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm hover:shadow-md hover:border-purple-200 dark:hover:border-purple-800 transition-all flex flex-col gap-3 overflow-hidden">
                                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-purple-400 to-blue-500" />
                                    <div className="flex justify-between items-start gap-4">
                                        <div>
                                            <h4 className="font-bold text-slate-800 dark:text-slate-100 text-sm group-hover:text-purple-700 dark:group-hover:text-purple-400 transition-colors">
                                                {reg.package?.package_name || "Paket Tidak Diketahui"}
                                            </h4>
                                            <p className="text-[11px] text-slate-500 mt-0.5 font-medium">{reg.period?.name || "-"}</p>
                                        </div>
                                        <Badge className={cn(
                                            "uppercase text-[9px] font-bold tracking-wider px-2 py-0.5 whitespace-nowrap border-none shadow-none",
                                            reg.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                                                reg.payment_status === 'pending' ? 'bg-amber-100 text-amber-700' :
                                                    'bg-slate-100 text-slate-600'
                                        )}>
                                            {reg.payment_status === 'paid' ? 'LUNAS' : reg.payment_status === 'pending' ? 'MENUNGGU' : 'BELUM BAYAR'}
                                        </Badge>
                                    </div>
                                    <div className="pt-3 mt-1 border-t border-slate-100 dark:border-slate-800 flex justify-between items-center text-xs">
                                        <span className="text-slate-400 font-mono tracking-wider">{reg.registration_number ? `#${reg.registration_number}` : "-"}</span>
                                        <span className="font-extrabold text-blue-600 dark:text-blue-400 text-sm bg-blue-50 dark:bg-blue-900/30 px-2 py-1 rounded-md">
                                            {reg.package?.currency} {Number(reg.package?.price || 0).toLocaleString('id-ID')}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </SectionBox>
                </div>
            )}

            {/* --- PIC INFORMATION --- */}
            {user?.role_id === 3 && (
                <div className="mt-4">
                    <div className="relative overflow-hidden rounded-[2rem] border border-amber-100 dark:border-amber-900/50 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-slate-900 dark:to-slate-900 shadow-sm p-6 md:p-8">
                        <div className="absolute top-0 right-0 h-40 w-40 bg-orange-500/10 rounded-full blur-[60px] -mr-10 -mt-10 pointer-events-none" />
                        <div className="relative z-10 flex flex-col md:flex-row items-center gap-6">
                            <div className="flex items-center justify-center w-14 h-14 rounded-2xl bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400">
                                <Briefcase className="w-7 h-7" />
                            </div>
                            <div className="text-left flex-1">
                                <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 mb-1">Area Person In Charge (PIC)</h3>
                                <p className="text-sm text-slate-600 dark:text-slate-400 max-w-2xl leading-relaxed">
                                    Sebagai PIC, Anda memiliki akses untuk mengelola pendaftaran delegasi dari institusi Anda secara kolektif.
                                    Gunakan menu sidebar untuk mengakses manajemen partisipan dan tagihan kolektif.
                                </p>
                            </div>
                            <Button onClick={() => navigate("/order/paket")} className="bg-orange-600 hover:bg-orange-700 text-white shadow-lg shadow-orange-600/20 rounded-full px-6 whitespace-nowrap">
                                Kelola Partisipan
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* --- FOOTER CTA SECTION --- */}
            {canOrderPackage ? (
                <div className="relative overflow-hidden rounded-[2rem] border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm p-6 md:p-10">
                    <div className="absolute top-0 right-0 h-48 w-48 bg-blue-600/5 rounded-full blur-[80px] -mr-20 -mt-20 pointer-events-none" />
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-500 to-purple-500" />
                    <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-8 text-left">
                        <div className="space-y-4">
                            <div className="space-y-1">
                                <p className="text-sm text-slate-500 dark:text-slate-400 max-w-2xl leading-relaxed font-medium italic">
                                    {t('profile.footer_msg')}
                                </p>
                            </div>
                        </div>

                        <div className="hidden md:block flex-shrink-0">
                            <button
                                onClick={() => navigate("/order/paket")}
                                className="group relative flex items-center gap-4 bg-blue-700 dark:bg-blue-600 hover:bg-blue-800 dark:hover:bg-blue-700 p-1 pr-6 rounded-3xl transition-all duration-300 shadow-lg shadow-slate-200 dark:shadow-none cursor-pointer border-none outline-none"
                            >
                                <div className="h-10 w-10 rounded-full bg-white/10 flex items-center justify-center text-white transition-transform group-hover:scale-110">
                                    <ShoppingBag className="w-5 h-5" />
                                </div>
                                <div className="text-left">
                                    <p className="text-[9px] font-bold text-blue-200/70 uppercase tracking-widest leading-none mb-1">{t('profile.footer_badge')}</p>
                                    <p className="text-xs font-bold text-white flex items-center gap-2">
                                        {t('profile.footer_action')}
                                        <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-1" />
                                    </p>
                                </div>
                            </button>
                        </div>
                    </div>
                </div>
            ) : null}


            {/* --- EDIT PROFILE DIALOG --- */}
            <Dialog open={isEditProfileOpen} onOpenChange={setIsEditProfileOpen}>
                <DialogContent className="sm:max-w-[600px] bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 rounded-3xl p-6 shadow-2xl">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-bold text-slate-900 dark:text-white">Edit Profil</DialogTitle>
                    </DialogHeader>
                    <div className="grid gap-4 py-4 max-h-[60vh] overflow-y-auto px-2">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="name" className="text-slate-700 dark:text-slate-300 font-semibold">Username / Display Name</Label>
                                <Input id="name" value={profileData.name} onChange={e => setProfileData({ ...profileData, name: e.target.value })} className="h-11 rounded-xl bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 focus-visible:ring-indigo-500" />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="email" className="text-slate-700 dark:text-slate-300 font-semibold">Email</Label>
                                <Input id="email" type="email" value={profileData.email} onChange={e => setProfileData({ ...profileData, email: e.target.value })} className="h-11 rounded-xl bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 focus-visible:ring-indigo-500" />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="full_name" className="text-slate-700 dark:text-slate-300 font-semibold">Nama Lengkap Partisipan</Label>
                                <Input id="full_name" value={profileData.full_name} onChange={e => setProfileData({ ...profileData, full_name: e.target.value })} className="h-11 rounded-xl bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 focus-visible:ring-indigo-500" />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="phone_mobile" className="text-slate-700 dark:text-slate-300 font-semibold">No. WhatsApp</Label>
                                <Input id="phone_mobile" value={profileData.phone_mobile} onChange={e => setProfileData({ ...profileData, phone_mobile: e.target.value })} className="h-11 rounded-xl bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 focus-visible:ring-indigo-500" />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="birth_place" className="text-slate-700 dark:text-slate-300 font-semibold">Tempat Lahir</Label>
                                <Input id="birth_place" value={profileData.birth_place} onChange={e => setProfileData({ ...profileData, birth_place: e.target.value })} className="h-11 rounded-xl bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 focus-visible:ring-indigo-500" />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="birth_date" className="text-slate-700 dark:text-slate-300 font-semibold">Tanggal Lahir</Label>
                                <Input id="birth_date" type="date" value={profileData.birth_date} onChange={e => setProfileData({ ...profileData, birth_date: e.target.value })} className="h-11 rounded-xl bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 focus-visible:ring-indigo-500" />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="institution" className="text-slate-700 dark:text-slate-300 font-semibold">Institusi</Label>
                                <Input id="institution" value={profileData.institution} onChange={e => setProfileData({ ...profileData, institution: e.target.value })} className="h-11 rounded-xl bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 focus-visible:ring-indigo-500" />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="position" className="text-slate-700 dark:text-slate-300 font-semibold">Jabatan / Pekerjaan</Label>
                                <Input id="position" value={profileData.position} onChange={e => setProfileData({ ...profileData, position: e.target.value })} className="h-11 rounded-xl bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 focus-visible:ring-indigo-500" />
                            </div>
                        </div>
                        <div className="space-y-2 mt-2">
                            <Label htmlFor="address" className="text-slate-700 dark:text-slate-300 font-semibold">Alamat Lengkap</Label>
                            <Input id="address" value={profileData.address} onChange={e => setProfileData({ ...profileData, address: e.target.value })} className="h-11 rounded-xl bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 focus-visible:ring-indigo-500" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsEditProfileOpen(false)} disabled={isSavingProfile}>Batal</Button>
                        <Button onClick={handleSaveProfile} disabled={isSavingProfile} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                            {isSavingProfile ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                            Simpan Perubahan
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* --- CHANGE PASSWORD DIALOG --- */}
            <Dialog open={isChangePasswordOpen} onOpenChange={setIsChangePasswordOpen}>
                <DialogContent className="sm:max-w-[400px] bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 rounded-3xl p-6 shadow-2xl">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-bold text-slate-900 dark:text-white">Ganti Password</DialogTitle>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="current_password" className="text-slate-700 dark:text-slate-300 font-semibold">Password Saat Ini</Label>
                            <Input id="current_password" type="password" value={passwordData.current_password} onChange={e => setPasswordData({ ...passwordData, current_password: e.target.value })} className="h-11 rounded-xl bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 focus-visible:ring-indigo-500" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="new_password" className="text-slate-700 dark:text-slate-300 font-semibold">Password Baru</Label>
                            <Input id="new_password" type="password" value={passwordData.new_password} onChange={e => setPasswordData({ ...passwordData, new_password: e.target.value })} className="h-11 rounded-xl bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 focus-visible:ring-indigo-500" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="new_password_confirmation" className="text-slate-700 dark:text-slate-300 font-semibold">Konfirmasi Password Baru</Label>
                            <Input id="new_password_confirmation" type="password" value={passwordData.new_password_confirmation} onChange={e => setPasswordData({ ...passwordData, new_password_confirmation: e.target.value })} className="h-11 rounded-xl bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 focus-visible:ring-indigo-500" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsChangePasswordOpen(false)} disabled={isSavingPassword}>Batal</Button>
                        <Button onClick={handleSavePassword} disabled={isSavingPassword} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                            {isSavingPassword ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                            Ganti Password
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

        </div>
    );
}

// --- Reusable Components ---

function SectionBox({ title, icon, children, className }: { title: string; icon: React.ReactNode; children: React.ReactNode; className?: string }) {
    return (
        <Card className={cn("border-0 shadow-sm bg-white dark:bg-gray-800 rounded-xl flex flex-col h-full", className)}>
            <CardHeader className="py-5 px-6 pb-2">
                <div className="flex items-center gap-2.5">
                    <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800">
                        {icon}
                    </div>
                    <CardTitle className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-[0.15em]">
                        {title}
                    </CardTitle>
                </div>
            </CardHeader>
            <CardContent className="px-6 py-4 pb-8 flex-1">
                {children}
            </CardContent>
        </Card>
    );
}

function InfoItem({ label, value, icon, isFullWidth }: { label: string; value?: string | number | null; icon?: React.ReactNode; isFullWidth?: boolean }) {
    const { t } = useTranslation('private');
    return (
        <div className={cn("flex flex-col gap-2 group", isFullWidth ? "sm:col-span-2" : "")}>
            {/* Label */}
            <div className="flex items-center gap-1.5 text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                {icon && <span>{React.cloneElement(icon as React.ReactElement, { size: 12 })}</span>}
                {label}
            </div>

            {/* Value (Read-Only Card Style) */}
            <div className="bg-slate-50/80 dark:bg-slate-900/50 border border-slate-100 dark:border-slate-800 rounded-xl px-4 py-3 min-h-[46px] flex items-center transition-colors group-hover:bg-slate-100/80 dark:group-hover:bg-slate-800/50">
                <span className="text-[13px] font-semibold text-slate-700 dark:text-slate-200 break-words leading-tight">
                    {value || <span className="text-slate-400 dark:text-slate-500 font-medium italic">{t('profile.not_available')}</span>}
                </span>
            </div>
        </div>
    );
}